import axios from 'axios'
import {
  baseURL
} from 'config'
import {
  jwtConfig,
  get,
  post
} from 'helpers/RequestUtil'
import {
  take,
  takeLatest,
  fork,
  all,
  select,
  cancel,
  cancelled,
  call,
  put,
} from 'redux-saga/effects'
import {
  TESTS,
  GREENLIGHT_STATES,
  COMPLETE,
  FETCH_REVISIONS,
  POST_APPROVALS,
  POST_APPROVALS_ERROR,
  CHANGE_REVISION,
  SUBMIT_APPROVAL,
  FETCH_PROJECT,
  ON_DIALOG_SUBMIT,
  ON_DIALOG_CANCEL
} from './constants'
import {
  MOUNT,
  UNMOUNT
} from 'containers/Project/GreenlightSection/constants'
import {
  clearProject,
  clearRevisions,
  clearCurrentRevision,
  fetchRevisionsStart,
  fetchRevisionsSuccess,
  fetchRevisionsError,
  fetchApprovalsStart,
  fetchApprovalsSuccess,
  fetchApprovalsError,
  postApprovalsStart,
  postApprovalsSuccess,
  postApprovalsError,
  postApprovals as postApprovalsAction,
  startFetchRevisions,
  startFetchApprovals,
  startPostApprovals,
  updateProject,
  updateRevisions,
  updateCurrentRevision,
  fetchProject as fetchProjectAction,
  fetchProjectStart,
  fetchProjectSuccess,
  fetchProjectError,
  startFetchProject,
  updateDialogActive,
  updateActionType,
} from './actions'
import {
  revisionSelector,
  revisionValueSelector,
  approvedSelector,
  revisionsStateSelector,
} from './selectors'
import * as R from 'ramda'

function* clean() {
  yield put(clearProject())
  yield put(clearRevisions())
  yield put(clearCurrentRevision())
  yield put(updateDialogActive(false))
  yield put(updateActionType(null))
}

function* fetchProject(projectId, revision, categoryName) {
  try {
    yield put(startFetchProject())
    yield put(fetchProjectStart())

    const query = `?project=${projectId}&revision=${revision}&categoryName=${categoryName}`
    const { data } = yield call(
      axios.get,
      `${baseURL}/api/fts/projectTests${query}`,
      jwtConfig()
    )

    

    yield put(fetchProjectSuccess())
    return data
  } catch(error) {
    yield put(fetchProjectError(error))
  }
}

function* fetchRevisions(projectId) {
  try {
    yield put(startFetchRevisions())
    yield put(fetchRevisionsStart())

    const { data } = yield call(
      axios.get,
      `${baseURL}/api/fts/projectRevisions?project=${projectId}`,
      jwtConfig()
    )

    yield put(fetchRevisionsSuccess(data))
    return data
  } catch(error) {
    yield put(fetchRevisionsError(error))
  }
}

function* fetchApprovals(projectId) {
  try {
    yield put(startFetchApprovals())
    yield put(fetchApprovalsStart())

    const data = yield call(get, `api/fulcrum/fts/revisions?filter[project_id]=${projectId}`)
    let {
      entities: {
        revisions: approvals
      }
    } = data

    if (approvals)
      approvals = Object
        .values(approvals)
        .reduce((acc, { revision: revisionId, state }) => R.assoc(revisionId, state, acc), {})
    else
      approvals = {}

    yield put(fetchApprovalsSuccess())
    return approvals
  } catch(error) {
    yield put(fetchApprovalsError(error))
  }
}

function* postApprovals(projectId, revision, state) {
  try {
    yield put(startPostApprovals())
    yield put(postApprovalsStart())
    yield call(post, 'revisions', { projectId, revision, state }, '/api/fulcrum/fts/revisions')
    yield put(postApprovalsSuccess())
  } catch(error) {
    yield put(postApprovalsError(error))
  }
}

function* fetchRevisionsHandler(action) {
  const { projectId } = action
  let revisions = yield call(fetchRevisions, projectId)
  let approvals = yield call(fetchApprovals, projectId)

  revisions = Array.isArray(revisions) && revisions.filter(({ status, categoryName }) => (
    status === COMPLETE && R.contains(categoryName, TESTS)
  ))

  const revisionsWithApprovals = revisions && revisions.reduce((final, {revision, ...rest}) => {
    final[revision] = {
      ...rest,
      revision,
      projectId,
      approved: R.ifElse(
        R.has(R.__, approvals),
        R.prop(R.__, approvals),
        R.always(GREENLIGHT_STATES.PENDING),
      )(revision)
    }
    return final
  }, {})

  yield put(updateRevisions(revisionsWithApprovals))
}

function* fetchProjectHandler(action) {
  const { projectId, revision, categoryName } = action
  const project = yield call(fetchProject, projectId, revision, categoryName)
  yield put(updateProject(revision, project))
}

function* postApprovalsHandler(action) {
  const { projectId, revision, state } = action
  yield call(postApprovals, projectId, revision, state)
  const revisions = yield select(revisionsStateSelector)
  const updatedRevisions = {
    ...revisions,
    [revision]: {
      ...revisions[revision],
      approved: state
    }
  }
  yield put(updateRevisions(updatedRevisions))
}

function* watchFetchRevisions() {
  yield takeLatest(FETCH_REVISIONS, fetchRevisionsHandler)
}

function* watchPostApprovals() {
  yield takeLatest(POST_APPROVALS, postApprovalsHandler)
}

function* watchFetchProject() {
  yield takeLatest(FETCH_PROJECT, fetchProjectHandler)
}

function* watchChangeRevision() {
  yield takeLatest(CHANGE_REVISION, function* (action) {
    const { value } = action
    yield put(updateCurrentRevision(value))
    const { projectId, revision, categoryName } = yield select(revisionValueSelector)
    yield put(fetchProjectAction(projectId, revision, categoryName))
  })
}

function* watchSubmitApproval() {
  while (true) {
    const { value } = yield take(SUBMIT_APPROVAL)
    yield put(updateDialogActive(true))
    yield put(updateActionType(value))
    const { type } = yield take([ON_DIALOG_CANCEL, ON_DIALOG_SUBMIT])
    if (type === ON_DIALOG_SUBMIT) {
      const { projectId, revision } = yield select(revisionValueSelector)
      yield put(postApprovalsAction(projectId, revision, value))
    }
    yield put(updateActionType(null))
    yield put(updateDialogActive(false))
  }
}

function* watchMounting() {
  while(true) {
    yield take([MOUNT, UNMOUNT])
    yield fork(clean)
  }
}

function* loadSagas() {
  yield all([
    fork(watchFetchProject),
    fork(watchFetchRevisions),
    fork(watchPostApprovals),
    fork(watchChangeRevision),
    fork(watchSubmitApproval),
  ])
}

export { watchMounting }
export default loadSagas
